import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { Settings, HelpCircle, Bell, User, LogOut, CheckSquare } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
// MVP: Commented out - not in scope for MVP
// import { BarChart3, Users, FileText } from 'lucide-react'

export default function Navbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isReviewer, logout } = useAuth()
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [viewedPages, setViewedPages] = useState<Set<string>>(new Set())
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Track viewed pages to remove notification badges
  useEffect(() => {
    const currentPath = location.pathname
    setViewedPages(prev => new Set([...prev, currentPath]))
  }, [location.pathname])

  const navItems = [
    {
      path: '/alerts',
      label: 'Alerts',
      icon: Bell,
      // MVP: Commented out dropdown - only 2 pages now
      // children: [
      //   { label: 'Overview', path: '/alerts', hasNotification: !viewedPages.has('/alerts') },
      // ],
    },
    // Reviewer-only navigation item
    ...(isReviewer ? [{
      path: '/review',
      label: 'Review',
      icon: CheckSquare,
    }] : []),
    // MVP: Commented out - not in scope for MVP
    // {
    //   path: '/customer-insights',
    //   label: 'Customer Insights',
    //   icon: Users,
    //   children: [
    //     { label: 'Dashboard', path: '/customer-insights', hasNotification: false },
    //     { label: 'Accounts', path: '/customer-insights/accounts', hasNotification: !viewedPages.has('/customer-insights/accounts') },
    //   ],
    // },
    // {
    //   path: '/product-insights',
    //   label: 'Product Insights',
    //   icon: BarChart3,
    //   children: [
    //     { label: 'Dashboard', path: '/product-insights', hasNotification: false },
    //     { label: 'Features Catalog', path: '/product-insights/features', hasNotification: !viewedPages.has('/product-insights/features') },
    //   ],
    // },
    {
      path: '/setup',
      label: 'Setup',
      icon: Settings,
      // MVP: Commented out dropdown - only 2 pages now
      // children: [
      //   { label: 'Integrations', path: '/setup#integrations', hasNotification: false },
      //   { label: 'Alert Notifications', path: '/setup#notifications', hasNotification: false },
      //   { label: 'Dashboard', path: '/setup#dashboard', hasNotification: false },
      // ],
    },
    // MVP: Commented out - not in scope for MVP
    // {
    //   path: '/reports',
    //   label: 'Reports',
    //   icon: FileText,
    //   children: [
    //     { label: 'Report', path: '/reports', hasNotification: false },
    //     { label: 'Dashboard', path: '/reports/dashboard', hasNotification: false },
    //   ],
    // },
  ]

  const handleMouseEnter = (itemPath: string) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setHoveredItem(itemPath)
  }

  const handleMouseLeave = () => {
    // Set a longer delay before closing
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
    }, 300)
  }

  const handleDropdownMouseEnter = () => {
    // Clear timeout when mouse enters dropdown
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const handleDropdownMouseLeave = () => {
    // Set timeout when mouse leaves dropdown
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
    }, 300)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <nav className="bg-white border-b border-gray-200 h-14 shadow-sm sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <div className="flex items-center">
            <img
              className="h-8 w-auto"
              src="/images/tandem-logo.svg"
              alt="Tandem Logo"
            />
            <span className="ml-2 text-xl font-bold text-gray-800">Tandem</span>
          </div>

          {/* Navigation Links with hover dropdowns - vertically centered */}
          <div className="hidden md:flex md:space-x-8">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname.startsWith(item.path)
              const isHovered = hoveredItem === item.path
              return (
                <div 
                  key={item.path} 
                  className="relative group"
                  onMouseEnter={() => handleMouseEnter(item.path)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={item.path}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-tandem-blue text-tandem-blue'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {item.label}
                  </Link>
                  {/* MVP: Commented out dropdown - only 2 pages now */}
                  {false && (
                    <div 
                      className={`absolute left-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition-all duration-200 z-50 ${
                        isHovered 
                          ? 'opacity-100 translate-y-0 pointer-events-auto' 
                          : 'opacity-0 translate-y-1 pointer-events-none'
                      }`}
                      onMouseEnter={handleDropdownMouseEnter}
                      onMouseLeave={handleDropdownMouseLeave}
                    >
                      <div className="py-1">
                        {/* Dropdown items commented out */}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              <HelpCircle className="w-4 h-4 mr-2 inline" />
              Resources
            </button>

            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <div className="w-8 h-8 bg-tandem-blue rounded-full flex items-center justify-center">
                  {user?.first_name || user?.email ? (
                    <span className="text-white text-xs font-medium">
                      {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                    </span>
                  ) : (
                    <User className="w-4 h-4 text-white" />
                  )}
                </div>
              </button>
              
              {/* User Menu Dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.first_name && user?.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user?.email || 'User'}
                      </p>
                      {user?.email && (
                        <p className="text-xs text-gray-500 mt-0.5">{user.email}</p>
                      )}
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu - basic list */}
      <div className="md:hidden">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname.startsWith(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive
                    ? 'bg-tandem-blue text-white'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  {item.label}
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
